import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Circle,
    LayersControl,
    MapContainer,
    Marker,
    Popup,
    TileLayer,
    useMap,
    useMapEvent,
} from 'react-leaflet';
import { divIcon } from 'leaflet';
import LocationFilter from './LocationFilter';
import { SiteResource } from './proptypes/site-resource';

const bulldozerIcon = () =>  divIcon({
    html: `<i class="pin fas fa-snowplow" style="color: #a8a8a8;"></i>`,
    iconSize: [15, 40],
    className: 'myDivIcon',
});
const pinIcon = (level, focus=false) => divIcon({
    html: `<i class="pin fas ${focus ? "fa-map-pin" : "fa-map-marker-alt"} level-${focus ? '4' : level}-pin "></i><p class="caption"></p>`,
    iconSize: [15, 40],
    className: 'myDivIcon',
});
const getIcon = (site) => {
    if (site.center) return pinIcon(site.level, true);

    if (site.is_private) return pinIcon('private');

    if (site.current_status === 'Demolished') return bulldozerIcon(site.center);

    return pinIcon(site.level);
};

const LevelPill = (props) => {
    const { userlevel = 1, isPrivate } = props;
    if (isPrivate) {
        return <span className={`badge private-pill`}>Private</span>;
    }

    return <span className={`badge level-${userlevel}-pill`}>Level {userlevel}</span>;
}

const CenterRadiusMarker = (props) => {
    const { center, setCenter, radius } = props;

    const onMove = ({ target: map }) => {
        const newCenter = map.getCenter();
        setCenter([newCenter.lat, newCenter.lng]);
    };

    useMapEvent('move', onMove)

    return <Circle center={center} radius={radius}/>;
}

const ChangeCenter = (props) => {
    const {
        center,
        zoom,
        recenter,
        setRecenter,
    } = props;
    if (!recenter) return null;
    const map = useMap();
    map.setView(center, zoom || 12);
    setRecenter(false);
    return null;
};

const getMapConfig = (arrayOfMarkers, centerSite) => {
    if (centerSite) {
        // override center if provided
        return { center: [centerSite.lat, centerSite.lng], zoom: 12 };
    } else if (arrayOfMarkers.length === 1) {
        // center and zoom out for one location
        return { center: arrayOfMarkers[0], zoom: 10 };
    } else if (arrayOfMarkers.length > 1) {
        // use bounds for multiple locations
        return { bounds: window.L.latLngBounds(arrayOfMarkers) };
    }
    // default
    return { center: [43.6532, -79.3832], zoom: 12 };
};

const getCenter = (bounds) => {
    const {
        _northEast,
        _southWest,
    } = bounds;

    return [
        (_northEast.lat - _southWest.lat)/2 + _southWest.lat,
        (_northEast.lng - _southWest.lng)/2 + _southWest.lng,
    ]
}

export const LocationVisualizer = (props) => {
    const {
        height,
        site,
        sites,
        options: {
            radiusSearch,
        },
        filterVisited,
    } = props;
    const [hideVisited, setVisited] = useState(false);

    const validSites = useMemo(() => {
        const allSites = site ? [{ center: true, ...site }, ...sites.filter(s => s.id !== site.id)] : sites;
        return allSites.filter(site => {
            return parseFloat(site.lat) !== 0
                && parseFloat(site.lng) !== 0
                && !(hideVisited && site.visited)
        });
    }, [site, sites, hideVisited]);

    const arrayOfMarkers = validSites.map(site => ([parseFloat(site.lat),parseFloat(site.lng)]));
    const mapConfig = getMapConfig(arrayOfMarkers, site);

    const onVisitedClick = (e) => {
        setVisited(e.target.checked);
    }

    // initial center setup
    useEffect(() => {
        if (!radiusSearch) return;
        radiusSearch.setCenter(mapConfig.center || getCenter(mapConfig.bounds));
    }, []);

    return (
      <div>
          <MapContainer
            id="visualize-location"
            zoomControl
            maxZoom={18}
            style={{ height }}
            {...mapConfig}
          >
              <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Google Hybrid">
                      <TileLayer
                        url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                        subdomains={['mt0','mt1','mt2','mt3']}
                      />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Google Streets">
                      <TileLayer
                        url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                        subdomains={['mt0','mt1','mt2','mt3']}
                      />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Google Satellite">
                      <TileLayer
                        url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                        subdomains={['mt0','mt1','mt2','mt3']}
                      />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Google Terrain">
                      <TileLayer
                        url="https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
                        subdomains={['mt0','mt1','mt2','mt3']}
                      />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Regular OpenStreetMap">
                      <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="B&W OpenStreetMap">
                      <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://tiles.wmflabs.org/bw-mapnik/{z}/{x}/{y}.png"
                      />
                  </LayersControl.BaseLayer>
              </LayersControl>
              <ChangeCenter {...radiusSearch} />
              {validSites.map((site, i) => (
                <Marker
                  key={`marker-${i}`}
                  icon={getIcon(site)}
                  position={[site.lat, site.lng]}
                >
                    {site.id && (
                        <Popup>
                            <p className="h4 mb-0">
                                <a target="_blank" href={site.slugUrl}>{site.title}</a>
                            </p>
                            <p className="m-0 fw-bold">#{site.id}</p>
                            <LevelPill userlevel={site.level} isPrivate={site.is_private}/>
                            <p className="mt-2 mb-0 fw-bold">
                                {site.county.name}, {site.state.short}
                                <a target="_blank" href={`https://maps.google.com/?q=${site.lat},${site.lng}`}>
                                    <i className="fas fa-external-link-alt ms-2"/>
                                </a>
                            </p>
                            <p className="m-0 fw-bold">{site.categories.map(c => c.name).join(', ') || 'Uncategorized'} - {site.current_status}</p>
                            <a target="_blank" href={site.slugUrl}>Browse</a>
                        </Popup>
                    )}
                </Marker>
              ))}
              {radiusSearch && <CenterRadiusMarker {...radiusSearch}/>}
              {filterVisited && <LocationFilter label="Hide Visited" onCheck={onVisitedClick} checked={hideVisited} />}
          </MapContainer>
      </div>
    )
};

LocationVisualizer.propTypes = {
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    site: PropTypes.shape(SiteResource),
    sites: PropTypes.arrayOf(PropTypes.shape(SiteResource)),
    options: PropTypes.shape({
        radiusSearch: PropTypes.shape({
            radius: PropTypes.number.isRequired,
            center: PropTypes.arrayOf(PropTypes.number),
            setCenter: PropTypes.func.isRequired,
        })
    }),
    filterVisited: PropTypes.bool,
};

LocationVisualizer.defaultProps = {
    height: 400,
    site: null,
    sites: [],
    options: {},
    filterVisited: false,
};
